import { ref } from 'vue'
import { systemConfigService, API_GATEWAY_BASE_URL } from '@/services/systemConfigService'

interface SystemSettings {
  id?: number | null
  systemName?: string
  systemDescription?: string
  logoUrl?: string | null
  poweredByLogoUrl?: string | null
  loginBannerUrl?: string | null
  headerBannerUrl?: string | null
  [key: string]: any
}

const systemSettings = ref<SystemSettings | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

export function useSystemSettings() {
  const fetchSystemSettings = async () => {
    if (loading.value) return

    loading.value = true
    error.value = null

    try {
      const data = await systemConfigService.getSystemSettings()
      if (data) {
        systemSettings.value = data
      }
    } catch (err: any) {
      console.error('Error fetching system settings:', err)
      error.value = err.message || 'Failed to load system settings'
    } finally {
      loading.value = false
    }
  }

  const getLogoUrl = (): string => {
    if (systemSettings.value?.logoUrl) {
      if (systemSettings.value.logoUrl.startsWith('http://') || systemSettings.value.logoUrl.startsWith('https://')) {
        return systemSettings.value.logoUrl
      }
      if (systemSettings.value.logoUrl.startsWith('/systemassets/')) {
        return systemConfigService.getAssetUrl(systemSettings.value.logoUrl)
      }
      if (systemSettings.value.logoUrl.startsWith('/')) {
        return `${API_GATEWAY_BASE_URL}${systemSettings.value.logoUrl}`
      }
      return `${API_GATEWAY_BASE_URL}/${systemSettings.value.logoUrl}`
    }
    return ''
  }

  const getLoginBannerUrl = (): string | null => {
    if (systemSettings.value?.loginBannerUrl) {
      if (
        systemSettings.value.loginBannerUrl.startsWith('http://') ||
        systemSettings.value.loginBannerUrl.startsWith('https://')
      ) {
        return systemSettings.value.loginBannerUrl
      }
      if (systemSettings.value.loginBannerUrl.startsWith('/systemassets/')) {
        return systemConfigService.getAssetUrl(systemSettings.value.loginBannerUrl)
      }
      if (systemSettings.value.loginBannerUrl.startsWith('/')) {
        return `${API_GATEWAY_BASE_URL}${systemSettings.value.loginBannerUrl}`
      }
      return `${API_GATEWAY_BASE_URL}/${systemSettings.value.loginBannerUrl}`
    }
    return null
  }

  const getHeaderBannerUrl = (): string | null => {
    if (systemSettings.value?.headerBannerUrl) {
      if (
        systemSettings.value.headerBannerUrl.startsWith('http://') ||
        systemSettings.value.headerBannerUrl.startsWith('https://')
      ) {
        return systemSettings.value.headerBannerUrl
      }
      if (systemSettings.value.headerBannerUrl.startsWith('/systemassets/')) {
        return systemConfigService.getAssetUrl(systemSettings.value.headerBannerUrl)
      }
      if (systemSettings.value.headerBannerUrl.startsWith('/')) {
        return `${API_GATEWAY_BASE_URL}${systemSettings.value.headerBannerUrl}`
      }
      return `${API_GATEWAY_BASE_URL}/${systemSettings.value.headerBannerUrl}`
    }
    return null
  }

  const getPoweredByLogoUrl = (): string | null => {
    if (systemSettings.value?.poweredByLogoUrl) {
      if (
        systemSettings.value.poweredByLogoUrl.startsWith('http://') ||
        systemSettings.value.poweredByLogoUrl.startsWith('https://')
      ) {
        return systemSettings.value.poweredByLogoUrl
      }
      if (systemSettings.value.poweredByLogoUrl.startsWith('/systemassets/')) {
        return systemConfigService.getAssetUrl(systemSettings.value.poweredByLogoUrl)
      }
      if (systemSettings.value.poweredByLogoUrl.startsWith('/')) {
        return `${API_GATEWAY_BASE_URL}${systemSettings.value.poweredByLogoUrl}`
      }
      return `${API_GATEWAY_BASE_URL}/${systemSettings.value.poweredByLogoUrl}`
    }
    return null
  }

  if (!systemSettings.value && !loading.value) {
    fetchSystemSettings()
  }

  return {
    systemSettings,
    loading,
    error,
    fetchSystemSettings,
    getLogoUrl,
    getLoginBannerUrl,
    getHeaderBannerUrl,
    getPoweredByLogoUrl,
  }
}
